import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Modal, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { PopupTaskSortPopBoxV3 } from '@components/Web/PopupTaskSortPopBoxV3.web';
import { TaskSortOption } from '@graphql/generated';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  onDismiss?: any;
  selectedSorts?: TaskSortOption[];
  onSelectedSorts?: (x: TaskSortOption[]) => void;
  allowedSortIds?: TaskSortOption[];
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
};

export const PopupTaskSortButtonV3 = ({
  onDismiss,
  boxProps: { position, offset, ...popupPropsRest },
  selectedSorts: xSelectedSorts = [],
  onSelectedSorts,
  allowedSortIds,
}: Props) => {
  const [visible, setVisible] = useState(false);

  const togglePopup = () => setVisible((v) => !v);
  const iconCloseRef = useRef<HTMLDivElement>();

  const ref2 = useRef<HTMLDivElement>();

  const isEventPointInArea = (
    ref: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = ref?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };
  const isEventPointInModal = (event: MouseEvent) => {
    return isEventPointInArea(ref2, event);
  };

  const isEventPointInCloseIcon = (event: MouseEvent) => {
    return isEventPointInArea(iconCloseRef, event);
  };
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (!isEventPointInModal(event)) {
        if (!isEventPointInCloseIcon(event)) {
          setVisible(false);
        } else {
          onSelectedSorts?.([]);
        }
      }
    },
    [iconCloseRef]
  );

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);
  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const badgeNumber = xSelectedSorts?.length;
  const [mouseOver, setMouseOver] = useState(false);

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    onDismiss?.();
    setVisible(false);
  };

  const backgroundColor1 = () => {
    if (mouseOver || badgeNumber !== 0 || visible) return 'grey01';
    return 'white';
  };

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel='Task List Sort'
        onPress={() => {
          togglePopup();
        }}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          py='xs'
          px='m'
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          onMouseLeave={() => {
            setMouseOver(false);
          }}
          backgroundColor={backgroundColor1()}
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'>
          <Icon variant='s' name='Sort' color='black' />
          <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
            Sort
          </Text>

          {badgeNumber > 0 ? (
            <Box ref={iconCloseRef}>
              <Icon
                marginLeft='xs'
                variant='xxxs'
                name='Close'
                onPress={() => {
                  onSelectedSorts?.([]);
                }}
                color='black'
              />
            </Box>
          ) : (
            <Box />
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          left='-200vw'
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupTaskSortPopBoxV3
            selectedSorts={xSelectedSorts}
            allowedSortIds={allowedSortIds}
            {...popupPropsRest}
          />
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            <PopupTaskSortPopBoxV3
              selectedSorts={xSelectedSorts}
              onSelectedSorts={onSelectedSorts}
              allowedSortIds={allowedSortIds}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
